import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import Subheader from "../../../components/ui/Subheader";
import { Button, Card, Input, Select, Space } from "antd";
import Search from "antd/es/input/Search";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import IconButton from "../../../components/common/IconButton";
import LeftArrowIcon from "../../../assets/icons/LeftArrowIcon";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import {
  forwardRequestToStepAboveAsync,
  getSingleBatchmentPaymentAsync,
  markPaidPaymentAsync,
  rejectBatchPaymentAsync,
} from "../../../store/features/NP/supplier/supplierAPI";
import { formatDate, formatDateNotNew } from "../../../utils/time";
import { IMAGE_URL } from "../../../helpers/config";
import useModal from "../../../hooks/useModal";
import EditIcon from "../../../assets/icons/EditIcon";
import ImportPaymentInvoiceModal from "../../../components/NP/Network/ImportPaymentInvoiceModal";
import LinkIcon from "../../../assets/icons/LinkIcon";
import Loader from "../../../components/common/Loader";
import ConfirmModal from "../../../components/common/ConfirmModal";
import dayjs from "dayjs";

export default function PaymentSummary() {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const [reason, setReason] = useState("");

  const {
    open: paymentStatusInvoiceOpen,
    toggle: paymentStatusInvoiceToggle,
    handleOpen: handlePaymentStatusInvoiceOpen,
  } = useModal();
  const {
    open: confirmOpen,
    toggle: confirmToggle,
    handleOpen: handleConfirmOpen,
  } = useModal();

  const { viewLoading, getSingleBatchmentPayment, loading } = useAppSelector(
    (state) => state.supplier
  );
  const { login } = useAppSelector((state) => state.login);

  const getData = useCallback(() => {
    if (id) dispatch(getSingleBatchmentPaymentAsync({ params: { id: id } }));
  }, [dispatch, id]);

  useEffect(() => {
    getData();
  }, [getData]);

  const vdsAmount = (getSingleBatchmentPayment?.totalAmount ?? 0) * 0.1;
  const invoiceAmount = getSingleBatchmentPayment?.totalAmount + vdsAmount;
  const tdsAmount = (getSingleBatchmentPayment?.totalAmount ?? 0) * 0.05;
  const netAmount = getSingleBatchmentPayment?.totalAmount - tdsAmount;

  const paymentDueDate = getSingleBatchmentPayment?.createdAt
    ? dayjs(getSingleBatchmentPayment.createdAt)
        .add(15, "day")
        .format("YYYY-MM-DD")
    : "N/A";

  const invoiceData = [
    {
      label1: "Vendor:",
      value1: `${getSingleBatchmentPayment?.vendor?.firstName || "N/A"} ${
        getSingleBatchmentPayment?.vendor?.lastName || ""
      }`,

      label2: "PO Number:",
      value2: getSingleBatchmentPayment?.po?.PONumber || "N/A",
      bg: "#F9FAFB",
    },
    {
      label1: "Batch Name:",
      value1: getSingleBatchmentPayment?.batchName || "N/A",
      label2: "GR Number:",
      value2: getSingleBatchmentPayment?.grInput || "N/A",
      bg: "#FFFFFF",
    },
    {
      label1: "Invoice Number:",
      value1: getSingleBatchmentPayment?.invoiceNo
        ? getSingleBatchmentPayment?.invoiceNo
        : "N/A",
      label2: "GR Date:",
      value2: getSingleBatchmentPayment?.grDate
        ? formatDateNotNew(getSingleBatchmentPayment?.grDate)
        : "N/A",
      bg: "#F9FAFB",
    },
    {
      label1: "Invoice Date:",
      value1: getSingleBatchmentPayment?.invoiceGenerateDate
        ? formatDateNotNew(getSingleBatchmentPayment?.invoiceGenerateDate)
        : "N/A",
      label2: "Payment Term:",
      value2: getSingleBatchmentPayment?.po?.paymentTermDays
        ? `${getSingleBatchmentPayment.po.paymentTermDays} days`
        : "N/A",
      bg: "#FFFFFF",
    },
    {
      label1: "Invoice Type:",
      value1: "N/A",
      label2: "Payment Due Date:",
      value2: paymentDueDate ? formatDate(paymentDueDate) : "N/A",
      bg: "#F9FAFB",
    },
    {
      label1: "Base Amount:",
      value1: `BDT ${(
        getSingleBatchmentPayment?.totalAmount ?? 0
      ).toLocaleString("en-IN")}`,
      label2: "",
      value2: "",
      bg: "#FFFFFF",
    },
  ];

  const paymentData = [
    {
      label1: "VDS Amount(10%):",
      value1: `BDT ${vdsAmount.toLocaleString("en-BD", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      label2: "Invoice Amount:",
      value2: `BDT ${invoiceAmount.toLocaleString("en-BD", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      bg: "#F9FAFB",
    },
    {
      label1: "TDS Amount(5%):",
      value1: `BDT ${tdsAmount.toLocaleString("en-BD", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      label2: "Net Payable:",
      value2: `BDT ${netAmount.toLocaleString("en-BD", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      bg: "#FFFFFF",
    },
  ];

  const handleRequest = (data: any) => {
    if (confirmOpen?.type === "PAYMENT" && login?.role === "finance") {
      const newValues = {
        remarkText: "Forwarding to next role",
      };

      dispatch(
        markPaidPaymentAsync({
          data: newValues,
          others: {
            reRender,
            id: data?._id,
            role: login?.role,
          },
        })
      );
    }
  };

  const handleReject = (data: any) => {
    if (confirmOpen?.type === "REJECT" && login?.role === "finance") {
      const newValues = {
        remarkText: reason,
      };

      dispatch(
        rejectBatchPaymentAsync({
          data: newValues,
          others: {
            reRender,
            id: data?._id,
          },
        })
      );
    }
  };

  function reRender() {
    getData();
    confirmToggle();
    setReason("");
  }

  return (
    <>
      {paymentStatusInvoiceOpen?.type === "IMPORT_PAYMENT_INVOICE" && (
        <ImportPaymentInvoiceModal
          open={paymentStatusInvoiceOpen?.open}
          data={paymentStatusInvoiceOpen?.data}
          handleClose={paymentStatusInvoiceToggle}
          getData={getData}
        />
      )}

      {confirmOpen?.type === "REJECT" && (
        <ConfirmModal
          open={confirmOpen?.open}
          data={confirmOpen?.data}
          handleClose={confirmToggle}
          deleteLoading={loading}
          handleYes={() => {}}
          buttonText=""
          title={""}
          hideAlert={true}
          deleteTitle=""
          questionText=""
          buttonFooterBlank={true}
        >
          <div className="self-stretch justify-start !text-[#2D3643] text-xl font-semibold ">
            Are You Sure You Want to Reject?
          </div>
          <div className="self-stretch justify-start !text-[#2D3643] text-base font-normal mt-2  ">
            Are you sure you want to reject those payment? This won’t be undone.
          </div>

          <div className="mt-5">
            <Input
              placeholder="Tell Us Why"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="w-full justify-end flex gap-2 pt-6">
            <Button onClick={() => confirmToggle()} className="cancel_btn">
              Cancel
            </Button>
            <Button
              disabled={!reason.trim()}
              className={`px-3.5 py-2 text-sm font-medium inline-flex items-center !border-none focus:!outline-none
                ${
                  reason.trim()
                    ? "bg-red-500 text-white hover:!bg-red-600 hover:!text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              onClick={() => handleReject(confirmOpen?.data)}
            >
              Reject
            </Button>
          </div>
        </ConfirmModal>
      )}

      {confirmOpen?.type === "PAYMENT" && (
        <ConfirmModal
          open={confirmOpen?.open}
          data={confirmOpen?.data}
          handleClose={confirmToggle}
          deleteLoading={loading}
          handleYes={handleRequest}
          buttonText="Approve"
          title={""}
          hideAlert={true}
          deleteTitle=""
          questionText=""
        >
          <div className="self-stretch justify-start !text-[#2D3643] text-xl font-semibold ">
            Are You Sure, You Want to Payment?
          </div>
          <div className="self-stretch justify-start !text-[#2D3643] text-base font-normal mt-2">
            Are you sure you want to approve this payment? This won't be undone.
          </div>
        </ConfirmModal>
      )}

      <AdminLayout title="Payment Summary" headerTitle="Payment">
        {viewLoading ? (
          <Loader />
        ) : (
          <section className="mt-7">
            <Subheader title="Payment Summary" subTitle={``}>
              <Space size="small" wrap>
                <>
                  {/* <h6 className="text-header text-base font-medium mr-6">
                  Balance: ৳ 65,56,524
                </h6> */}
                  {/* <Select
                    placeholder="Zone"
                    allowClear
                    showSearch
                    // filterOption={selectSearchOption}
                    // options={ArrayOption(areas, "areaName", "_id", true)}
                    // onChange={(val) => {
                    //   setAreaId(val);
                    //   setTerritoryId(undefined);
                    // }}
                    // value={areaId}
                    // disabled={!regionId}
                    className="min-w-[80px]"
                    popupMatchSelectWidth={120}
                  /> */}

                  {/* <Select
                    //   value={regionId}
                    placeholder="Cluster"
                    allowClear
                    showSearch
                    //   filterOption={selectSearchOption}
                    //   options={ArrayOption(regions, "regionName", "_id")}
                    //   onChange={(val) => {
                    //     setRegionId(val);
                    //     setAreaId(undefined);
                    //     setTerritoryId(undefined);
                    //   }}
                    className="min-w-[80px]"
                    //   disabled={login?.role === "region" || login?.role === "finance"}
                    popupMatchSelectWidth={120}
                  /> */}

                  {/* <Select
                    placeholder="Status"
                    allowClear
                    showSearch
                    //   filterOption={selectSearchOption}
                    options={[{ label: "Pending", value: "pending" }]}
                    // onChange={(val) => {
                    //   setTerritoryId(val);
                    // }}
                    // value={territoryId}
                    // disabled={!areaId}
                    className="min-w-[80px]"
                    optionFilterProp="children"
                    popupMatchSelectWidth={120}
                  /> */}
                </>
              </Space>
            </Subheader>

            <Card className="mt-6">
              <div className="flex justify-between items-center">
                <div>
                  {/* <Search
                    placeholder="Search by batch name"
                    // onSearch={(val) => {
                    //   handleEnterValue(val);
                    //   handleSetFirstPage(true);
                    // }}
                    // onChange={(val) => {
                    //   handleInputChange(val);
                    //   handleSetFirstPage(true);
                    // }}
                    className="custom-search-input max-w-[200px]"
                  /> */}
                </div>

                <div className="flex gap-2">
                  <Link to={`/finance/payment/details/${id}`}>
                    <Button className="w-[100px] text-green-600 font-medium px-3 py-1.5 bg-teal-50 rounded-lg inline-flex justify-center items-center">
                      Details
                    </Button>
                  </Link>

                  <Button
                    type="primary"
                    disabled={
                      login?.role !== getSingleBatchmentPayment?.currentRole ||
                      getSingleBatchmentPayment?.status === "rejected" ||
                      getSingleBatchmentPayment?.status === "passed" ||
                      getSingleBatchmentPayment?.status === "paid"
                    }
                    onClick={() =>
                      handleConfirmOpen({
                        type: "PAYMENT",
                        data: getSingleBatchmentPayment,
                      })
                    }
                  >
                    Approve
                  </Button>

                  <Button
                    disabled={
                      getSingleBatchmentPayment?.status === "rejected" ||
                      getSingleBatchmentPayment?.status === "passed" ||
                      getSingleBatchmentPayment?.status === "paid" ||
                      login?.role !== getSingleBatchmentPayment?.currentRole
                    }
                    onClick={() =>
                      handleConfirmOpen({
                        type: "REJECT",
                        data: getSingleBatchmentPayment,
                      })
                    }
                    className="px-3.5 py-2 !bg-red-500 !text-white text-sm font-medium inline-flex items-center !border-none 
        hover:!bg-red-600 hover:!text-white hover:!border-none focus:!outline-none"
                  >
                    Reject
                  </Button>

                  <IconButton
                    className="!border-others-header !text-sm !font-normal !rounded-lg !border !text-header !bg-transparent"
                    type="default"
                    icon={<LeftArrowIcon />}
                    onClick={() => navigate(-1)}
                  >
                    <span>Back</span>
                  </IconButton>
                </div>
              </div>
            </Card>

            <div className="self-stretch p-2.5 bg-green-600 rounded inline-flex justify-start items-center gap-2.5 mt-6 w-full">
              <div className="justify-start text-white text-lg font-medium">
                Invoice Information
              </div>
            </div>

            <Card className="mt-6">
              {invoiceData.map((row, index) => (
                <div
                  key={index}
                  className="grid grid-cols-4 items-center gap-2 mb-2 mt-2"
                >
                  <div
                    className="justify-start text-slate-600 text-base font-medium text-right p-2"
                    style={{ backgroundColor: row.bg }}
                  >
                    {row.label1}
                  </div>
                  <div
                    className="justify-start text-slate-600 text-base font-normal p-2"
                    style={{ backgroundColor: row.bg }}
                  >
                    {row.value1}
                  </div>
                  <div
                    className="justify-start text-slate-600 text-base font-medium text-right p-2"
                    style={{ backgroundColor: row.bg }}
                  >
                    {row.label2}
                  </div>
                  <div
                    className="justify-start text-slate-600 text-base font-normal p-2"
                    style={{ backgroundColor: row.bg }}
                  >
                    {row.value2}
                  </div>
                </div>
              ))}
            </Card>

            <div className="self-stretch p-2.5 bg-green-600 rounded inline-flex justify-start items-center gap-2.5 mt-6 w-full">
              <div className="justify-start text-white text-lg font-medium">
                Payment Details
              </div>
            </div>

            <Card className="mt-6">
              {paymentData.map((row, index) => (
                <div
                  key={index}
                  className="grid grid-cols-4 items-center gap-2 mb-2 mt-2"
                >
                  <div
                    className="justify-start text-slate-600 text-base font-medium text-right p-2"
                    style={{ backgroundColor: row.bg }}
                  >
                    {row.label1}
                  </div>
                  <div
                    className="justify-start text-slate-600 text-base font-normal p-2"
                    style={{ backgroundColor: row.bg }}
                  >
                    {row.value1}
                  </div>
                  <div
                    className="justify-start text-slate-600 text-base font-medium text-right p-2"
                    style={{ backgroundColor: row.bg }}
                  >
                    {row.label2}
                  </div>
                  <div
                    className="justify-start text-slate-600 text-base font-normal p-2"
                    style={{ backgroundColor: row.bg }}
                  >
                    {row.value2}
                  </div>
                </div>
              ))}
            </Card>

            <div className="self-stretch p-2.5 bg-green-600 rounded inline-flex justify-start items-center gap-2.5 mt-6 w-full">
              <div className="justify-start text-white text-lg font-medium">
                Attachments
              </div>
            </div>

            <div className="mt-6 mb-6 flex justify-start items-center gap-3">
              {getSingleBatchmentPayment?.invoiceUrl && (
                <a
                  href={getSingleBatchmentPayment?.invoiceUrl}
                  target="_blank"
                  download
                  rel="noreferrer"
                  className="p-3 cursor-pointer bg-white rounded-[10px] border border-[#E5E7EB] shadow-md inline-flex justify-start items-center gap-4"
                >
                  <div className="text-slate-600 text-base font-medium">
                    Invoice
                  </div>
                  <div className="mt-2 text-gray-600 hover:text-gray-600">
                    <DownloadIcon />
                  </div>
                </a>
              )}

              {getSingleBatchmentPayment?.vat && (
                <div className="p-3 cursor-pointer text-gray-600 hover:text-gray-600 bg-white rounded-[10px] border border-[#E5E7EB] shadow-md inline-flex justify-start items-center gap-4">
                  <a
                    href={IMAGE_URL + getSingleBatchmentPayment?.vat}
                    download
                    // target="_blank"
                    rel="noreferrer"
                    className="text-slate-600 text-base font-medium"
                  >
                    VAT_6.3
                  </a>
                  <div className="mt-2 text-gray-600 hover:text-gray-600">
                    <DownloadIcon />
                  </div>
                </div>
              )}

              {getSingleBatchmentPayment?.financeOne?.map(
                (item: any, index: number) => (
                  <div
                    key={index}
                    className="p-3 cursor-pointer text-gray-600 hover:text-gray-600 bg-white rounded-[10px] border border-[#E5E7EB] shadow-md inline-flex justify-start items-center gap-4"
                  >
                    <a
                      href={IMAGE_URL + item}
                      download
                      rel="noreferrer"
                      className="text-slate-600 text-base font-medium"
                    >
                      VAT_6.3
                    </a>
                    <div className="mt-2 text-gray-600 hover:text-gray-600">
                      <DownloadIcon />
                    </div>
                  </div>
                )
              )}

              <div
                onClick={() =>
                  handlePaymentStatusInvoiceOpen({
                    type: "IMPORT_PAYMENT_INVOICE",
                    data: getSingleBatchmentPayment,
                  })
                }
                className="p-3 cursor-pointer !text-[#33AB29] hover:text-[#33AB29] bg-white rounded-[10px] border border-[#E5E7EB] shadow-md inline-flex justify-start items-center gap-4"
              >
                <div className="mt-2 !text-[#33AB29] hover:text-[#33AB29]">
                  <LinkIcon />
                </div>
                <div className="!text-[#33AB29] hover:text-[#33AB29] text-base font-medium">
                  Add
                </div>
              </div>
            </div>
          </section>
        )}
      </AdminLayout>
    </>
  );
}
